import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import RestaurantSetupWizard from "./RestaurantSetupWizard";
import {
  finishRestaurantSetupTour,
  readRestaurantSetupTour,
  updateRestaurantReservationTourStep
} from "../utils/restaurantSetupTour";

const TOUR_Z_INDEX = 2050;
const CARD_WIDTH = 390;
const CARD_ESTIMATED_HEIGHT = 300;
const EDGE_GAP = 16;

const InteractionBlocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${TOUR_Z_INDEX};
  pointer-events: auto;
  background: transparent;
`;

const Highlight = styled.div`
  position: fixed;
  z-index: ${TOUR_Z_INDEX + 1};
  pointer-events: none;
  border: 3px solid #ee754c;
  border-radius: 10px;
  box-shadow:
    0 0 0 5px rgba(238, 117, 76, 0.2),
    0 0 0 9999px rgba(33, 25, 26, 0.6);
  transition: all 0.18s ease;
`;

const FallbackBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${TOUR_Z_INDEX + 1};
  pointer-events: none;
  background: rgba(33, 25, 26, 0.6);
`;

const TourCard = styled.div`
  position: fixed;
  z-index: ${TOUR_Z_INDEX + 2};
  width: min(${CARD_WIDTH}px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  padding: 19px;
  background: #ffffff;
  border: 1px solid #eaded9;
  border-radius: 14px;
  box-shadow: 0 20px 55px rgba(33, 25, 26, 0.28);
  color: #49383b;
  font-family: "Poppins", sans-serif;
`;

const StepLabel = styled.div`
  margin-bottom: 6px;
  color: #ee754c;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 0 0 8px;
  color: var(--app-orange, #ee754c);
  font-size: 1.12rem;
  font-weight: 850;
`;

const Body = styled.p`
  margin: 0;
  color: #65595b;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
`;

const Button = styled.button`
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const SecondaryButton = styled(Button)`
  margin-right: auto;
  background: #eef2f1;
  color: #596b68;
`;

const PrimaryButton = styled(Button)`
  background: #ee754c;
  color: #ffffff;
`;

const LinkBox = styled.div`
  margin-top: 14px;
  padding: 11px;
  border: 1px solid #e5ddda;
  border-radius: 9px;
  background: #fbf9f8;
`;

const PublicLink = styled.div`
  overflow-wrap: anywhere;
  color: #564a4c;
  font-size: 0.78rem;
  line-height: 1.45;
`;

const LinkActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f0f4f3;
  color: #526461;
  font-size: 0.78rem;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    color: #354542;
  }
`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getCardPosition = rect => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(CARD_WIDTH, viewportWidth - EDGE_GAP * 2);
  let top;

  if (rect.bottom + CARD_ESTIMATED_HEIGHT + EDGE_GAP < viewportHeight) {
    top = rect.bottom + EDGE_GAP;
  } else if (rect.top - CARD_ESTIMATED_HEIGHT - EDGE_GAP > 0) {
    top = rect.top - CARD_ESTIMATED_HEIGHT - EDGE_GAP;
  } else {
    top = EDGE_GAP;
  }

  const left = clamp(
    rect.left + rect.width / 2 - cardWidth / 2,
    EDGE_GAP,
    Math.max(EDGE_GAP, viewportWidth - cardWidth - EDGE_GAP)
  );

  return {
    top: clamp(
      top,
      EDGE_GAP,
      Math.max(EDGE_GAP, viewportHeight - CARD_ESTIMATED_HEIGHT - EDGE_GAP)
    ),
    left
  };
};

const buildReservationSteps = restaurantId => [
  {
    key: "create",
    selector: '[data-setup-tour="reservations-create"]',
    titleKey: "ui.restaurantReservationTour.create.title",
    bodyKey: "ui.restaurantReservationTour.create.body"
  },
  {
    key: "calendar",
    selector: '[data-setup-tour="reservations-calendar"]',
    titleKey: "ui.restaurantReservationTour.calendar.title",
    bodyKey: "ui.restaurantReservationTour.calendar.body"
  },
  {
    key: "status",
    selector: '[data-setup-tour="reservations-status"]',
    titleKey: "ui.restaurantReservationTour.status.title",
    bodyKey: "ui.restaurantReservationTour.status.body"
  },
  {
    key: "list",
    selector: '[data-setup-tour="reservations-list"]',
    titleKey: "ui.restaurantReservationTour.list.title",
    bodyKey: "ui.restaurantReservationTour.list.body"
  },
  {
    key: "link",
    selector: null,
    titleKey: "ui.restaurantReservationTour.link.title",
    bodyKey: "ui.restaurantReservationTour.link.body",
    restaurantId
  }
];

const RestaurantReservationTour = ({ tour }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [targetRect, setTargetRect] = useState(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: EDGE_GAP, left: EDGE_GAP });
  const [copied, setCopied] = useState(false);
  const steps = useMemo(
    () => buildReservationSteps(tour.restaurantId),
    [tour.restaurantId]
  );
  const currentIndex = clamp(Number(tour.reservationStep || 0), 0, steps.length - 1);
  const currentStep = steps[currentIndex];
  const reservationPath = `/reservationfromrestaurant/${tour.restaurantId}/null/filter`;

  useEffect(() => {
    if (location.pathname !== reservationPath) {
      navigate(reservationPath, { replace: true });
    }
  }, [location.pathname, navigate, reservationPath]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const preventEscape = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("keydown", preventEscape, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", preventEscape, true);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
  }, [currentIndex]);

  useEffect(() => {
    if (!currentStep?.selector || location.pathname !== reservationPath) {
      setTargetRect(null);
      setTargetMissing(false);
      return undefined;
    }

    let attempts = 0;
    let intervalId;
    let resizeObserver;
    let targetElement = null;

    const readRect = element => {
      const rect = element.getBoundingClientRect();
      const paddedRect = {
        top: Math.max(4, rect.top - 5),
        left: Math.max(4, rect.left - 5),
        width: Math.max(10, rect.width + 10),
        height: Math.max(10, rect.height + 10),
        right: rect.right + 5,
        bottom: rect.bottom + 5
      };
      setTargetRect(paddedRect);
      setCardPosition(getCardPosition(paddedRect));
    };

    const updateTarget = () => {
      const element = document.querySelector(currentStep.selector);
      attempts += 1;
      if (!element) {
        if (attempts >= 35) {
          setTargetRect(null);
          setTargetMissing(true);
          window.clearInterval(intervalId);
        }
        return;
      }

      targetElement = element;
      setTargetMissing(false);
      element.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: attempts === 1 ? "smooth" : "auto"
      });
      window.setTimeout(() => {
        if (targetElement && document.body.contains(targetElement)) {
          readRect(targetElement);
        }
      }, 100);
      window.clearInterval(intervalId);

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          if (targetElement && document.body.contains(targetElement)) {
            readRect(targetElement);
          }
        });
        resizeObserver.observe(targetElement);
      }
    };

    intervalId = window.setInterval(updateTarget, 120);
    updateTarget();

    const handleViewportChange = () => {
      if (targetElement && document.body.contains(targetElement)) {
        readRect(targetElement);
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.clearInterval(intervalId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [currentStep, location.pathname, reservationPath]);

  const goTo = nextIndex => {
    const safeIndex = clamp(nextIndex, 0, steps.length - 1);
    updateRestaurantReservationTourStep(tour.restaurantId, safeIndex);
  };

  const handleNext = () => {
    if (currentIndex >= steps.length - 1) {
      finishRestaurantSetupTour();
      return;
    }
    goTo(currentIndex + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) goTo(currentIndex - 1);
  };

  const publicLink = `${window.location.origin}/#/reservations/${tour.restaurantId}/client/no_id`;
  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const googleLanguage = language === "pt" ? "pt" : language === "es" ? "es" : "en";
  const googleGuide = `https://support.google.com/business/answer/6218037?hl=${googleLanguage}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
    } catch (error) {
      const textarea = document.createElement("textarea");
      textarea.value = publicLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
      setCopied(true);
    }
  };

  const fallbackPosition = {
    top: Math.max(EDGE_GAP, window.innerHeight / 2 - CARD_ESTIMATED_HEIGHT / 2),
    left: Math.max(
      EDGE_GAP,
      window.innerWidth / 2 -
        Math.min(CARD_WIDTH, window.innerWidth - EDGE_GAP * 2) / 2
    )
  };
  const position =
    targetRect && !targetMissing && currentStep.selector ? cardPosition : fallbackPosition;

  return (
    <>
      <InteractionBlocker />
      {targetRect && !targetMissing && currentStep.selector ? (
        <Highlight
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height
          }}
        />
      ) : (
        <FallbackBackdrop />
      )}

      <TourCard
        style={{ top: position.top, left: position.left }}
        role="dialog"
        aria-modal="true"
        aria-live="polite"
        aria-label={t("ui.restaurantReservationTour.ariaLabel")}
      >
        <StepLabel>
          {t("ui.restaurantReservationTour.step", {
            current: currentIndex + 1,
            total: steps.length
          })}
        </StepLabel>
        <Title>{t(currentStep.titleKey)}</Title>
        <Body>{t(currentStep.bodyKey)}</Body>

        {targetMissing && currentStep.selector && (
          <Body style={{ marginTop: "10px", fontSize: "0.8rem" }}>
            {t("ui.restaurantReservationTour.targetUnavailable")}
          </Body>
        )}

        {currentStep.key === "link" && (
          <>
            <LinkBox>
              <PublicLink>{publicLink}</PublicLink>
              <LinkActions>
                <Button
                  type="button"
                  style={{ background: "#ee754c", color: "white" }}
                  onClick={copyLink}
                >
                  {copied
                    ? t("ui.restaurantReservationTour.link.copied")
                    : t("ui.restaurantReservationTour.link.copy")}
                </Button>
                <ExternalLink href={publicLink} target="_blank" rel="noreferrer">
                  {t("ui.restaurantReservationTour.link.open")}
                </ExternalLink>
              </LinkActions>
            </LinkBox>
            <Body style={{ marginTop: "12px" }}>
              {t("ui.restaurantReservationTour.link.websiteExample")}
            </Body>
            <ExternalLink
              href={googleGuide}
              target="_blank"
              rel="noreferrer"
              style={{ marginTop: "12px" }}
            >
              {t("ui.restaurantReservationTour.link.googleGuide")}
            </ExternalLink>
          </>
        )}

        <Footer>
          <SecondaryButton type="button" onClick={handleBack} disabled={currentIndex === 0}>
            {t("ui.restaurantReservationTour.back")}
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleNext}>
            {currentIndex === steps.length - 1
              ? t("ui.restaurantReservationTour.finish")
              : t("ui.restaurantReservationTour.next")}
          </PrimaryButton>
        </Footer>
      </TourCard>
    </>
  );
};

const RestaurantSetupTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tour, setTour] = useState(() => readRestaurantSetupTour());

  const syncFromStorage = useCallback(() => {
    setTour(readRestaurantSetupTour());
  }, []);

  useEffect(() => {
    syncFromStorage();
  }, [location.pathname, syncFromStorage]);

  useEffect(() => {
    const handleStorage = event => {
      if (!event.key || event.key === "soup_restaurant_setup_tour") {
        syncFromStorage();
      }
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("restaurant-setup-tour-change", syncFromStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("restaurant-setup-tour-change", syncFromStorage);
    };
  }, [syncFromStorage]);

  useEffect(() => {
    if (!tour?.active || !tour.restaurantId) return;
    if (tour.phase === "setup") {
      const setupPath = `/place_design/${tour.restaurantId}`;
      if (location.pathname !== setupPath) {
        navigate(setupPath, { replace: true });
      }
    }
  }, [tour?.active, tour?.phase, tour?.restaurantId, location.pathname, navigate]);

  if (!tour?.active || !tour?.restaurantId) return null;

  if (tour.phase === "reservation") {
    return <RestaurantReservationTour tour={tour} />;
  }

  return <RestaurantSetupWizard tour={tour} />;
};

export default RestaurantSetupTour;
